import { Bold, Eraser, Italic, Link, List, ListOrdered, Redo2, Table2, Underline, Undo2 } from "lucide-react";
import { useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { applyEditorAction, createHistory, isSafeHref, pushHistory, redoHistory, undoHistory, type EditorAction, type HistoryState, type Selection } from "./editorActions";
import { MarkdownRenderer } from "./MarkdownRenderer";

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  label: string;
  name?: string;
  rows?: number;
};

type ToolbarAction = { label: string; action?: EditorAction; icon?: ReactNode };

const primaryToolbarActions: ToolbarAction[] = [
  { label: "Bold", action: { type: "bold" }, icon: <Bold /> },
  { label: "Italic", action: { type: "italic" }, icon: <Italic /> },
  { label: "Underline", action: { type: "underline" }, icon: <Underline /> },
  { label: "Bulleted list", action: { type: "bulletList" }, icon: <List /> },
  { label: "Numbered list", action: { type: "numberedList" }, icon: <ListOrdered /> },
  { label: "Link", icon: <Link /> },
];

export function MarkdownEditor({ value, onChange, maxLength, label, name, rows = 5 }: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectionRef = useRef<Selection>({ start: value.length, end: value.length });
  const historyRef = useRef<HistoryState>(createHistory());
  const [history, setHistory] = useState<HistoryState>(historyRef.current);
  const updateHistory = (next: HistoryState) => { historyRef.current = next; setHistory(next); };

  const focusSelection = (selection: Selection) => {
    selectionRef.current = selection;
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(selection.start, selection.end);
    });
  };

  const commit = (nextValue: string, selection: Selection, record = true) => {
    const limited = nextValue.slice(0, maxLength);
    const nextSelection = { start: Math.min(selection.start, limited.length), end: Math.min(selection.end, limited.length) };
    if (limited !== value) {
      if (record) updateHistory(pushHistory(historyRef.current, { value, selection: selectionRef.current }));
      onChange(limited);
    }
    focusSelection(nextSelection);
  };

  const runAction = (action: EditorAction) => {
    const result = applyEditorAction(value, selectionRef.current, action);
    commit(result.value, result.selection);
  };

  const runLink = () => {
    const href = window.prompt("Enter a link URL", "https://");
    if (href && isSafeHref(href)) runAction({ type: "link", href });
  };

  const undo = () => {
    const result = undoHistory(historyRef.current, { value, selection: selectionRef.current });
    if (result.snapshot.value === value && result.snapshot.selection.start === selectionRef.current.start && result.snapshot.selection.end === selectionRef.current.end) return;
    updateHistory(result.history);
    onChange(result.snapshot.value);
    focusSelection(result.snapshot.selection);
  };

  const redo = () => {
    const result = redoHistory(historyRef.current, { value, selection: selectionRef.current });
    if (result.snapshot.value === value && result.snapshot.selection.start === selectionRef.current.start && result.snapshot.selection.end === selectionRef.current.end) return;
    updateHistory(result.history);
    onChange(result.snapshot.value);
    focusSelection(result.snapshot.selection);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.currentTarget.value.slice(0, maxLength);
    if (nextValue === value) return;
    const previousSelection = selectionRef.current;
    updateHistory(pushHistory(historyRef.current, { value, selection: previousSelection }));
    selectionRef.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd };
    onChange(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const modifier = event.metaKey || event.ctrlKey;
    if (!modifier) return;
    if (event.key.toLowerCase() === "z" && !event.shiftKey) { event.preventDefault(); undo(); }
    else if ((event.key.toLowerCase() === "z" && event.shiftKey) || event.key.toLowerCase() === "y") { event.preventDefault(); redo(); }
  };

  const preventBlur = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault();
  return <div className="markdown-editor">
    <div className="markdown-toolbar" role="toolbar" aria-label={`${label} formatting`}>
      {primaryToolbarActions.map(({ label: actionLabel, action, icon }) => <button key={actionLabel} className="markdown-tool" type="button" title={actionLabel} aria-label={actionLabel} onMouseDown={preventBlur} onClick={() => action ? runAction(action) : runLink()}>{icon ?? actionLabel.replace("Heading ", "H")}</button>)}
      <details className="markdown-secondary">
        <summary aria-label="More formatting">More</summary>
        <div className="markdown-secondary-menu">
          <label className="markdown-style-control">Text style<select aria-label="Text style" defaultValue="0" onChange={(event) => runAction({ type: "heading", level: Number(event.target.value) as 0 | 1 | 2 | 3 | 4 | 5 | 6 })}><option value="0">Paragraph</option>{[1, 2, 3, 4, 5, 6].map((level) => <option key={level} value={level}>Heading {level}</option>)}</select></label>
          <button className="markdown-tool" type="button" title="2×2 table" aria-label="2×2 table" onMouseDown={preventBlur} onClick={() => runAction({ type: "table" })}><Table2 />Table</button>
          <button className="markdown-tool" type="button" title="Clear formatting" aria-label="Clear formatting" onMouseDown={preventBlur} onClick={() => runAction({ type: "clear" })}><Eraser />Clear</button>
        </div>
      </details>
      <span className="markdown-toolbar-spacer" />
      <button className="markdown-tool" type="button" title="Undo" aria-label="Undo" disabled={!history.past.length} onMouseDown={preventBlur} onClick={undo}><Undo2 /></button>
      <button className="markdown-tool" type="button" title="Redo" aria-label="Redo" disabled={!history.future.length} onMouseDown={preventBlur} onClick={redo}><Redo2 /></button>
    </div>
    <div className="markdown-editor-grid">
      <div className="markdown-input-wrap">
        <textarea ref={textareaRef} id={name ? `markdown-${name}` : undefined} name={name} aria-label={label} value={value} maxLength={maxLength} rows={rows} onChange={handleChange} onKeyDown={handleKeyDown} onSelect={(event) => { selectionRef.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd }; }} />
        <small className="markdown-count">{value.length.toLocaleString()} / {maxLength.toLocaleString()}</small>
      </div>
      <MarkdownRenderer value={value} aria-label={`${label} live preview`} className="markdown-preview" />
    </div>
  </div>;
}
